(() => {
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  function setCartCount(quantity) {
    qsa('[data-cart-count]').forEach((node) => { node.textContent = quantity; });
  }

  async function refreshCartCount() {
    const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    setCartCount(cart.item_count);
  }

  function openMenu() {
    const overlay = qs('#MenuOverlay');
    if (!overlay) return;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('menu-open');
    qs('[data-close-menu]', overlay)?.focus();
  }

  function closeMenu() {
    const overlay = qs('#MenuOverlay');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('menu-open');
  }

  function openSizeGuide() {
    closeMenu();
    const dialog = qs('#SizeGuide');
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.body.classList.add('dialog-open');
  }

  function closeSizeGuide() {
    const dialog = qs('#SizeGuide');
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.body.classList.remove('dialog-open');
  }

  function parseVariants(productRoot) {
    const script = qs('[data-product-variants]', productRoot);
    if (!script) return [];
    try { return JSON.parse(script.textContent); } catch (_) { return []; }
  }

  function updateProduct(productRoot) {
    const variants = parseVariants(productRoot);
    const groups = qsa('[data-option-index]', productRoot).filter((node) => node.classList.contains('sizes'));
    const selected = groups.map((group) => {
      const active = qs('[data-product-option][aria-pressed="true"]', group);
      return active ? active.dataset.optionValue : null;
    });

    qsa('[data-product-option]', productRoot).forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      const possible = variants.some((variant) => variant.available && variant.options.every((value, index) => {
        if (index === optionIndex) return value === button.dataset.optionValue;
        return !selected[index] || value === selected[index];
      }));
      button.disabled = !possible;
      button.classList.toggle('is-unavailable', !possible);
    });

    const variant = groups.length === 0
      ? variants.find((item) => item.available) || variants[0]
      : selected.every(Boolean)
        ? variants.find((item) => item.options.every((value, index) => value === selected[index]))
        : null;
    const idInput = qs('[name="id"]', productRoot);
    const addButton = qs('[data-add-to-cart]', productRoot);
    const error = qs('[data-product-error]', productRoot);
    if (error) error.hidden = true;
    if (!idInput || !addButton) return;

    if (!variant) {
      idInput.disabled = true;
      addButton.disabled = true;
      addButton.textContent = 'SELECT A SIZE';
      return;
    }

    idInput.value = variant.id;
    idInput.disabled = !variant.available;
    addButton.disabled = !variant.available;
    addButton.dataset.added = 'false';
    addButton.textContent = variant.available ? `ADD TO CART · ${variant.priceFormatted}` : 'SOLD OUT';

    const price = qs('[data-product-price]', productRoot);
    const compare = qs('[data-product-compare]', productRoot);
    const sale = qs('[data-product-sale]', productRoot);
    if (price) price.textContent = variant.priceFormatted;
    if (price) price.classList.toggle('regular', !(variant.compareAtPrice > variant.price));
    if (compare) {
      compare.textContent = variant.compareAtPriceFormatted || '';
      compare.hidden = !(variant.compareAtPrice > variant.price);
    }
    if (sale) sale.hidden = !(variant.compareAtPrice > variant.price);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  function initializeProduct(productRoot) {
    const variants = parseVariants(productRoot);
    const selectedId = Number(productRoot.dataset.selectedVariant || new URLSearchParams(location.search).get('variant'));
    const selectedVariant = variants.find((item) => item.id === selectedId);
    if (selectedVariant) {
      qsa('[data-product-option]', productRoot).forEach((button) => {
        const active = selectedVariant.options[Number(button.dataset.optionIndex)] === button.dataset.optionValue;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.classList.toggle('active', active);
      });
    }
    updateProduct(productRoot);
  }

  async function addProduct(form) {
    const productRoot = form.closest('[data-product-root]');
    const button = qs('[data-add-to-cart]', form);
    const error = qs('[data-product-error]', form);
    if (!button || button.disabled) return;
    if (button.dataset.added === 'true') {
      window.location.href = window.UnlistedTheme?.cartUrl || `${root()}cart`;
      return;
    }

    button.disabled = true;
    button.textContent = 'ADDING…';
    if (error) error.hidden = true;
    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'This item could not be added.');
      await refreshCartCount();
      button.disabled = false;
      button.dataset.added = 'true';
      button.textContent = 'ADDED — VIEW CART';
    } catch (message) {
      button.disabled = false;
      button.textContent = 'ADD TO CART';
      if (error) {
        error.textContent = message.message || 'This item could not be added.';
        error.hidden = false;
      }
    }
  }

  async function changeCartLine(button) {
    const line = Number(button.dataset.cartLine);
    const quantity = Number(button.dataset.cartQuantity);
    if (!line || Number.isNaN(quantity)) return;
    button.disabled = true;
    try {
      const response = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!response.ok) throw new Error('Cart update failed');
      window.location.reload();
    } catch (_) {
      button.disabled = false;
      const message = qs('[data-cart-error]');
      if (message) {
        message.textContent = 'Could not update the cart. Please try again.';
        message.hidden = false;
      }
    }
  }

  document.addEventListener('click', (event) => {
    const menuOpen = event.target.closest('[data-open-menu]');
    const menuClose = event.target.closest('[data-close-menu]');
    const guideOpen = event.target.closest('[data-open-size-guide]');
    const guideClose = event.target.closest('[data-close-size-guide]');
    const calculatorToggle = event.target.closest('[data-toggle-calculator]');
    const calculatorButton = event.target.closest('[data-calculate-size]');
    const optionButton = event.target.closest('[data-product-option]');
    const cartButton = event.target.closest('[data-cart-line]');

    if (menuOpen) openMenu();
    if (menuClose) closeMenu();
    if (guideOpen) openSizeGuide();
    if (guideClose) closeSizeGuide();
    if (calculatorToggle) {
      const calculator = qs('.size-calculator', calculatorToggle.closest('dialog'));
      if (calculator) calculator.hidden = !calculator.hidden;
    }
    if (calculatorButton) {
      const dialog = calculatorButton.closest('dialog');
      const height = Number(qs('[data-height]', dialog)?.value);
      const weight = Number(qs('[data-weight]', dialog)?.value);
      const maxHeight = Number(qs('[data-m-max-height]', dialog)?.textContent) || 178;
      const maxWeight = Number(qs('[data-m-max-weight]', dialog)?.textContent) || 78;
      const result = qs('[data-size-result]', dialog);
      if (result) result.textContent = height && weight ? `SUGGESTED SIZE: ${height <= maxHeight && weight <= maxWeight ? 'M' : 'L'}` : 'ENTER YOUR HEIGHT AND WEIGHT';
    }
    if (optionButton && !optionButton.disabled) {
      const productRoot = optionButton.closest('[data-product-root]');
      const group = optionButton.closest('[data-option-index]');
      qsa('[data-product-option]', group).forEach((button) => {
        const active = button === optionButton;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.classList.toggle('active', active);
      });
      updateProduct(productRoot);
    }
    if (cartButton) changeCartLine(cartButton);
  });

  document.addEventListener('submit', (event) => {
    if (!event.target.matches('[data-product-form]')) return;
    event.preventDefault();
    addProduct(event.target);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeMenu();
  });

  qsa('[data-product-root]').forEach(initializeProduct);
  qs('#SizeGuide')?.addEventListener('close', () => document.body.classList.remove('dialog-open'));
})();
